import { DATE_INPUT_MSG, dateShortOf, dateValueOf, typeDate } from "@genbaryoku/contract";
import { useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { useNarrow } from "../lib/useNarrow.js";
import { currentView } from "../lib/view.js";
import { CalendarPop } from "./CalendarPop.jsx";
import { CalendarIcon } from "./icons.jsx";

/**
 * 日付の欄（受け取った書類 S3＝../eigyouryoku/docs/13 §4.5 の 5、★10＝ほかの日付の欄にもいっしょに当てる。docs/10 §5 の入力の決めごと）。
 *   PC：文字の欄（inputmode="numeric"・等幅・`26/10/09` の形＝#320 で年2桁。乗せると `2026/10/09`）＋右端のカレンダーのアイコン（藍）→ 自前のカレンダーの窓（CalendarPop の field＝マス 44px）。
 *     打ったものは contract の typeDate で直す（全角・`20261009`・`2026-10-9`・`R8.10.9`・`令和8年10月9日`・`H30/1/5`・年2桁の `26/10/09`・`261009`）。
 *     読める形になった瞬間に onChange（自動保存の画面は打ったまま保存される）。欄を離れたとき・Enter で `YY/MM/DD` に整える。
 *     読めなければ打った文字のまま残して朱の1行「日付になっていません」（値は変えない）。空にすると onChange("")（受けるかは使う側）
 *   スマホ版（html[data-view="phone"]）と幅 767px 以下：OS の日付の欄のまま（type="date"＝iPhone はホイール。今の欄を残す）
 *   値の受け渡しはいつも `YYYY-MM-DD`（今の type="date" と同じ）＝保存の形・自動保存の作法は変えない。読むだけ（readOnly）はカレンダーを開かない
 *   新しく日付の欄を作るときはこれを使う（CLAUDE.md「入力の決めごと」）
 */
export function DateInput({
  value,
  onChange,
  label,
  id,
  ariaLabel,
  readOnly = false,
  disabled = false,
  clearable = true,
  inline = false,
  invalid,
  max,
  min,
  className,
  style,
  placeholder,
  testId,
}: {
  /** `YYYY-MM-DD` か空 */
  value: string | null | undefined;
  /** `YYYY-MM-DD` か ""（空にした） */
  onChange: (v: string) => void;
  /** 欄の名前（カレンダーのアイコンの読み上げ「〇〇をカレンダーから選択」と窓の名前） */
  label: string;
  id?: string;
  /** 名前の <label for> が無い欄の読み上げ */
  ariaLabel?: string;
  readOnly?: boolean;
  disabled?: boolean;
  /** 窓に「日付削除」を出すか（空にできない欄は false） */
  clearable?: boolean;
  /** 見積・請求書の頭の帯・表の中のような低い欄（.inp を付けない。高さは置いた所の CSS） */
  inline?: boolean;
  invalid?: boolean;
  max?: string;
  min?: string;
  /** 欄（input）に足すクラス */
  className?: string;
  /** 外枠（.dti）の style（幅など） */
  style?: CSSProperties;
  /** 空のときの字（{order}の条件の「未定」＝押せない欄。#258） */
  placeholder?: string | undefined;
  testId?: string;
}) {
  const narrow = useNarrow();
  const os = narrow || currentView() === "phone";
  const wrap = useRef<HTMLSpanElement>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const [err, setErr] = useState(false);
  const [open, setOpen] = useState(false);
  const v = value ?? "";
  const inpClass = (inline ? "dti-in" : "inp dti-in") + (className ? ` ${className}` : "");
  const wrapClass = "dti" + (inline ? " is-inline" : "") + (err ? " is-error" : "");

  if (os) {
    return (
      <span className={wrapClass + " is-os"} style={style}>
        <input
          type="date"
          className={inpClass}
          id={id}
          value={v}
          aria-label={ariaLabel}
          aria-invalid={invalid || undefined}
          readOnly={readOnly}
          disabled={disabled}
          placeholder={placeholder}
          max={max}
          min={min}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      </span>
    );
  }

  /** 打った文字を確かめる。読めれば値を送る（同じなら送らない） */
  const take = (raw: string): string | null => {
    const t = typeDate(raw);
    if (t === null) return null;
    const next = t === "" ? "" : dateValueOf(t);
    if (next !== v) onChange(next);
    return t;
  };
  const settle = () => {
    if (draft == null) return;
    const t = take(draft);
    if (t === null) setErr(true);
    else {
      setErr(false);
      setDraft(null);
    }
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing && draft != null) {
      const t = take(draft);
      if (t === null) setErr(true);
      else {
        setErr(false);
        setDraft(dateShortOf(t));
      }
    } else if (e.key === "ArrowDown" && e.altKey && !readOnly && !disabled) {
      e.preventDefault();
      setOpen(true);
    }
  };

  return (
    <span ref={wrap} className={wrapClass} style={style} data-fit-one="">
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        spellCheck={false}
        className={inpClass}
        id={id}
        value={draft ?? dateShortOf(v)}
        title={v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v.replace(/-/g, "/") : undefined}
        aria-label={ariaLabel}
        aria-invalid={err || invalid || undefined}
        readOnly={readOnly}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => {
          setDraft(e.target.value);
          if (take(e.target.value) !== null) setErr(false);
        }}
        onBlur={settle}
        onKeyDown={onKey}
        data-testid={testId}
      />
      {readOnly ? null : (
        <button
          type="button"
          className="dti-cal"
          aria-label={DATE_INPUT_MSG.pick(label)}
          aria-haspopup="dialog"
          aria-expanded={open}
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          data-testid={testId ? `${testId}-cal` : undefined}
        >
          <CalendarIcon size={inline ? 18 : 22} />
        </button>
      )}
      {err ? (
        <span className="dti-err" role="alert" data-testid={testId ? `${testId}-error` : undefined}>
          {DATE_INPUT_MSG.invalid}
        </span>
      ) : null}
      {open && wrap.current ? (
        <CalendarPop
          variant="field"
          value={/^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null}
          anchor={wrap.current}
          label={label}
          clearable={clearable}
          onPick={(next) => {
            setOpen(false);
            setDraft(null);
            setErr(false);
            if ((next ?? "") !== v) onChange(next ?? "");
            wrap.current?.querySelector<HTMLInputElement>("input")?.focus({ preventScroll: true });
          }}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </span>
  );
}
